/**
 * Onboarding journey guard — persists progress flags in sessionStorage so
 * route guards (beforeLoad) can verify that the user really came from
 * pricing → checkout before reaching business / channel creation.
 */
import { useSyncExternalStore } from "react";
import type { TranslateFn } from "@/i18n/translate";

export type JourneyFlag = "planSelected" | "paymentDone" | "businessCreated";

const KEY_BASE = "lov:onboarding:flags";
const LEGACY_KEY = "lov:onboarding:flags";
const EVT = "lov:onboarding:flags-changed";

export type Flags = Record<JourneyFlag, boolean>;

const empty: Flags = {
  planSelected: false,
  paymentDone: false,
  businessCreated: false,
};

let activeWorkspaceId = "";

function isBrowser() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function keyFor(workspaceId: string): string {
  return workspaceId ? `${KEY_BASE}:${workspaceId}` : KEY_BASE;
}

function currentKey(): string {
  return keyFor(activeWorkspaceId);
}

function emitChange() {
  if (!isBrowser()) return;
  try {
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {
    /* ignore */
  }
}

function migrateLegacyFlags(workspaceId: string) {
  if (!isBrowser() || !workspaceId) return;
  try {
    const legacy = window.sessionStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const target = keyFor(workspaceId);
    if (window.sessionStorage.getItem(target) === null) {
      window.sessionStorage.setItem(target, legacy);
    }
    window.sessionStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Scopes all flag reads/writes to a workspace. Called by WorkspaceProvider
 * whenever the active workspace changes so multiple businesses keep separate
 * onboarding progress.
 */
export function setActiveJourneyWorkspace(workspaceId: string) {
  if (activeWorkspaceId === workspaceId) return;
  activeWorkspaceId = workspaceId;
  migrateLegacyFlags(workspaceId);
  emitChange();
}

export function getFlags(): Flags {
  if (!isBrowser()) return { ...empty };
  try {
    const raw = window.sessionStorage.getItem(currentKey());
    if (!raw) return { ...empty };
    return { ...empty, ...JSON.parse(raw) };
  } catch {
    return { ...empty };
  }
}

export function setFlag(flag: JourneyFlag, value = true) {
  if (!isBrowser()) return;
  const next = { ...getFlags(), [flag]: value };
  try {
    window.sessionStorage.setItem(currentKey(), JSON.stringify(next));
    emitChange();
  } catch {
    /* ignore */
  }
}

export function resetFlags() {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(currentKey());
    emitChange();
  } catch {
    /* ignore */
  }
}

/** Hook returning the parsed flags object (re-renders on change) */
export function useJourneyFlags(): Flags {
  const json = useSyncExternalStore(
    (cb) => {
      if (!isBrowser()) return () => {};
      const listener = () => cb();
      window.addEventListener(EVT, listener);
      window.addEventListener("storage", listener);
      return () => {
        window.removeEventListener(EVT, listener);
        window.removeEventListener("storage", listener);
      };
    },
    () => JSON.stringify(getFlags()),
    () => JSON.stringify(empty),
  );
  try {
    return JSON.parse(json) as Flags;
  } catch {
    return { ...empty };
  }
}

export interface JourneyStep {
  key: "plan" | "checkout" | "business" | "channels";
  label: string;
  href: string;
  lockFlag?: JourneyFlag;
}

export const JOURNEY_STEPS: readonly JourneyStep[] = [
  { key: "plan", label: "انتخاب پلن", href: "/dashboard/pricing" },
  { key: "checkout", label: "پرداخت", href: "/dashboard/plan/checkout", lockFlag: "planSelected" },
  {
    key: "business",
    label: "ساخت کسب‌وکار",
    href: "/onboarding/business",
    lockFlag: "paymentDone",
  },
  {
    key: "channels",
    label: "اتصال کانال‌ها",
    href: "/onboarding/channels",
    lockFlag: "businessCreated",
  },
];

/** What's required for each step (the previous flag must be true) */
export const STEP_PREREQ: Record<"checkout" | "business" | "channels", JourneyFlag[]> = {
  checkout: ["planSelected"],
  business: ["planSelected", "paymentDone"],
  channels: ["planSelected", "paymentDone", "businessCreated"],
};

export const FLAG_LABEL: Record<JourneyFlag, string> = {
  planSelected: "انتخاب پلن",
  paymentDone: "تکمیل پرداخت",
  businessCreated: "ساخت کسب‌وکار",
};

/** First missing prerequisite for a target step (or null if all met). */
export function firstMissing(
  step: keyof typeof STEP_PREREQ,
  flags = getFlags(),
): JourneyFlag | null {
  for (const f of STEP_PREREQ[step]) if (!flags[f]) return f;
  return null;
}

export function lockedStepMessage(
  t: TranslateFn,
  flag?: JourneyFlag,
): { title: string; description?: string } {
  return {
    title: t("dashboard.journey.lockedToastTitle", "ابتدا مرحله قبل را تکمیل کنید"),
    description: flag
      ? t(
          "dashboard.journey.lockedToastDesc",
          "برای فعال شدن این بخش باید «{flag}» انجام شود.",
          { flag: t(`dashboard.journey.${flag}`, FLAG_LABEL[flag]) },
        )
      : undefined,
  };
}
